import React, { FunctionComponent, useContext } from "react";
import { FormattedMessage } from "react-intl";
import { DrugCostInfo, Plan } from "../../../@types";
import { AppStore } from "../../../app/store";
import { hasMailOrderPharmacy } from "../../../app/store/AppStore";
import { CollapsibleBox, RenderedTable } from "../../../components";
import {
  $$,
  addRowAttributes,
  formatDate,
  IntlContext,
  planHasDrugCoverage,
  shouldSuppressDrugPricing,
  useBelowLargeBreakpoint,
  useTableConfig,
  useTranslate,
  getCellStringValueFromHeaderProps,
} from "../../../helpers";
import { PharmacyNetworkStatusStackedHeading } from "../components";
import { Ga4Event } from "../../../helpers/analytics";
import {
  AugmentedColumn,
  AugmentedHeaderProps,
  col,
} from "../../../@types/react-table";
import { CellValue, TableInstance } from "react-table";

export const rowNames = {
  yearlyDrugCosts: "yearlyDrugCosts",
  whenYoullMeetYourDeductible: "whenYoullMeetYourDeductible",
  whenYoullEnterCoverageGap: "whenYoullEnterCoverageGap",
  whenYoullGetOutOfCoverageGap: "whenYoullGetOutOfCoverageGap",
};

const {
  yearlyDrugCosts,
  whenYoullMeetYourDeductible,
  whenYoullEnterCoverageGap,
  whenYoullGetOutOfCoverageGap,
} = rowNames;

export const EstimatedTotalDrugAndPremiumCostTable: FunctionComponent<{
  drugCostInfo: DrugCostInfo;
  plan: Plan;
}> = ({ drugCostInfo, plan }) => {
  const t = useTranslate();
  const fm = useContext(IntlContext).formatMessage;
  const {
    state: { language },
    state,
  } = useContext(AppStore.AppContext);
  const includesMailOrder = hasMailOrderPharmacy(state);
  const suppressDrugPricing = shouldSuppressDrugPricing(plan);
  const premium = plan.total_remaining_premium;
  const planHasZeroDrugDeductible = plan.drug_plan_deductible === 0;
  const thisPlanHasDrugCoverage = planHasDrugCoverage(plan);

  const optionalContentProps = {};
  useBelowLargeBreakpoint(matches => {
    optionalContentProps["aria-hidden"] = matches;
  }, []);

  const yearlyDrugCostsRow = {
    label: t("plan_details.total_yearly_drug_and_premium_cost"),
    name: yearlyDrugCosts,
  };
  const whenYoullMeetYourDeductibleRow = {
    label: t("plan_details.when_you_will_meet_your_deductible"),
    name: whenYoullMeetYourDeductible,
  };
  const whenYoullEnterCoverageGapRow = {
    label: (
      <CollapsibleBox
        labelId="plan_details.when_will_you_enter_the_coverage_gap"
        titleClassname="when_will_you_enter_the_coverage_gap__heading"
        tealiumProps={{
          event_action: "Plan Details Subheader Help Caret clicked",
          event_label: "when_will_you_enter_the_coverage_gap",
        }}
        ga4Props={{
          event_name: Ga4Event.PLAN_DETAILS_COVERAGE_GAP,
        }}
      >
        {t("plan_details.when_will_you_enter_the_coverage_gap.details")}
      </CollapsibleBox>
    ),
    name: whenYoullEnterCoverageGap,
  };
  const whenYoullGetOutOfCoverageGapRow = {
    label: t("plan_details.when_will_you_get_out_of_the_coverage_gap"),
    name: whenYoullGetOutOfCoverageGap,
  };

  const tableRows = [
    yearlyDrugCostsRow,
    whenYoullMeetYourDeductibleRow,
    whenYoullEnterCoverageGapRow,
    whenYoullGetOutOfCoverageGapRow,
  ];

  let data = useTableConfig(tableRows, [drugCostInfo, language]);

  const pharmacyCols = [{ accessor: "label" }] as AugmentedColumn[];

  let noPharmacyHasGapStart = true;

  drugCostInfo.costs.forEach(pharmacy => {
    if (pharmacy.npi || includesMailOrder) {
      pharmacyCols.push({
        Header: (props: AugmentedHeaderProps<col>) => {
          return (
            <PharmacyNetworkStatusStackedHeading
              drugCostInfo={drugCostInfo}
              includeName
              isTableHeader
              npi={pharmacy.npi}
              hasDrugCoverage={thisPlanHasDrugCoverage}
              // Add costs to stacked view, sr-only content
              stackedViewScreenReaderContent={getCellStringValueFromHeaderProps(
                props,
                fm
              )}
            />
          );
        },
        Cell: function cellRenderer({
          value,
          row,
        }: Partial<TableInstance> | CellValue) {
          if (
            !row.attributes ||
            !Object.prototype.hasOwnProperty.call(row.attributes, "data-testid")
          ) {
            addRowAttributes(row, {
              "data-testid": row?.original?.name,
            });
          }
          // `aria-hide` cell value below large breakpoint. This content is in
          // sr-visible output of the pharamacy network status component
          return <div {...optionalContentProps}>{value}</div>;
        },
        accessor: pharmacy.npi || "mailOrder",
      });
      const { phase_information } = pharmacy;
      const {
        deductible_amount,
        initial_coverage_start,
        gap_start,
        catastrophic_start,
      } = phase_information;

      const pharmacyHasDeductible = Boolean(deductible_amount);
      const hasInitialCoverage = Boolean(initial_coverage_start);
      const hasGapStart = Boolean(gap_start);
      const hasCatostrophicStart = Boolean(catastrophic_start);

      if (hasGapStart) {
        noPharmacyHasGapStart = false;
      }

      if (pharmacy.mail_order) {
        yearlyDrugCostsRow["mailOrder"] = $$(
          pharmacy.estimated_yearly_total + premium
        );

        if (pharmacyHasDeductible) {
          if (hasInitialCoverage) {
            whenYoullMeetYourDeductibleRow["mailOrder"] = formatDate(
              initial_coverage_start,
              "MMMM y",
              state.language
            );
          } else if (hasGapStart) {
            whenYoullMeetYourDeductibleRow["mailOrder"] = formatDate(
              gap_start,
              "MMMM y",
              language
            );
          } else {
            whenYoullMeetYourDeductibleRow["mailOrder"] = (
              <FormattedMessage
                id="plan_details.you_will_not_meet_your_deductible_in"
                values={{
                  year: drugCostInfo.plan.contract_year,
                }}
              />
            );
          }
        } else {
          whenYoullMeetYourDeductibleRow["mailOrder"] = (
            <FormattedMessage
              id="plan_details.you_will_not_meet_your_deductible_in"
              values={{
                year: drugCostInfo.plan.contract_year,
              }}
            />
          );
        }

        whenYoullEnterCoverageGapRow["mailOrder"] = hasGapStart ? (
          formatDate(gap_start, "MMMM y", language)
        ) : (
          <FormattedMessage
            id="plan_details.you_will_not_enter_the_coverage_gap_in"
            values={{
              year: drugCostInfo.plan.contract_year,
            }}
          />
        );

        whenYoullGetOutOfCoverageGapRow["mailOrder"] = hasCatostrophicStart ? (
          formatDate(catastrophic_start, "MMMM y", language)
        ) : (
          <FormattedMessage
            id="plan_details.you_will_not_get_out_of_the_coverage_gap_in"
            values={{
              year: drugCostInfo.plan.contract_year,
            }}
          />
        );

        if (suppressDrugPricing) {
          yearlyDrugCostsRow["mailOrder"] = t("na");
          whenYoullMeetYourDeductibleRow["mailOrder"] = t("na");
          whenYoullEnterCoverageGapRow["mailOrder"] = t("na");
          whenYoullGetOutOfCoverageGapRow["mailOrder"] = t("na");
        }
      } else {
        yearlyDrugCostsRow[pharmacy.npi] = $$(
          pharmacy.estimated_yearly_total + premium
        );

        if (pharmacyHasDeductible) {
          if (hasInitialCoverage) {
            whenYoullMeetYourDeductibleRow[pharmacy.npi] = formatDate(
              initial_coverage_start,
              "MMMM y",
              language
            );
          } else if (hasGapStart) {
            whenYoullMeetYourDeductibleRow[pharmacy.npi] = formatDate(
              gap_start,
              "MMMM y",
              language
            );
          } else {
            whenYoullMeetYourDeductibleRow[pharmacy.npi] = (
              <FormattedMessage
                id="plan_details.you_will_not_meet_your_deductible_in"
                values={{
                  year: drugCostInfo.plan.contract_year,
                }}
              />
            );
          }
        } else {
          whenYoullMeetYourDeductibleRow[pharmacy.npi] = (
            <FormattedMessage
              id="plan_details.you_will_not_meet_your_deductible_in"
              values={{
                year: drugCostInfo.plan.contract_year,
              }}
            />
          );
        }

        whenYoullEnterCoverageGapRow[pharmacy.npi] = hasGapStart ? (
          formatDate(gap_start, "MMMM y", language)
        ) : (
          <FormattedMessage
            id="plan_details.you_will_not_enter_the_coverage_gap_in"
            values={{
              year: drugCostInfo.plan.contract_year,
            }}
          />
        );

        whenYoullGetOutOfCoverageGapRow[pharmacy.npi] = hasCatostrophicStart ? (
          formatDate(catastrophic_start, "MMMM y", language)
        ) : (
          <FormattedMessage
            id="plan_details.you_will_not_get_out_of_the_coverage_gap_in"
            values={{
              year: drugCostInfo.plan.contract_year,
            }}
          />
        );

        if (suppressDrugPricing) {
          yearlyDrugCostsRow[pharmacy.npi] = t("na");
          whenYoullMeetYourDeductibleRow[pharmacy.npi] = t("na");
          whenYoullEnterCoverageGapRow[pharmacy.npi] = t("na");
          whenYoullGetOutOfCoverageGapRow[pharmacy.npi] = t("na");
        }
      }
    }
  });

  const columns = useTableConfig(pharmacyCols, [drugCostInfo, language]);

  // Remove `whenYoullMeetYourDeductibleRow` for plans with no drug deductible
  if (planHasZeroDrugDeductible) {
    data = data.filter(
      ({ name }) => name !== whenYoullMeetYourDeductibleRow.name
    );
  }

  // Remove `whenYoullGetOutOfCoverageGapRow` when no pharmacy has `gap_start`
  if (noPharmacyHasGapStart) {
    data = data.filter(
      ({ name }) => name !== whenYoullGetOutOfCoverageGapRow.name
    );
  }

  return (
    <RenderedTable
      id="EstimatedTotalDrugAndPremiumCostTable"
      className={`pharmacy-column-table__${drugCostInfo.costs.length || 1}`}
      tableColumns={columns}
      tableData={data}
      caption={t("plan_terms.estimated_yearly_total_cost")}
      borderless
      rowHeaders
      stackableBreakpoint="lg"
    />
  );
};
